import { FetchUserAttributesOutput } from "aws-amplify/auth";
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import Select, { ClassNamesConfig, GroupBase } from "react-select";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  SearchIndicator,
  createClassNames,
} from "@/components/ui/react-select";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateUser } from "../api/update-user";
import {
  BasicUserInformationFormData,
  basicUserInformationSchema,
} from "../types/basic-user-information.schema";

type BasicUserInformationFormProps = {
  initialData: FetchUserAttributesOutput & { timeZone?: string };
  isEditingOwnUser: boolean;
};

interface TimezoneOption {
  value: string;
  label: string;
}

// Get all timezones and format them for the select
// @ts-expect-error - Somehow Intl.supportedValuesOf is not available in the type definitions,
const timezoneOptions: TimezoneOption[] = Intl.supportedValuesOf(
  "timeZone",
).map((timezone: string) => ({
  value: timezone,
  label: timezone.replace(/_/g, " "),
}));

function transformApiDataToFormData(
  initialData?: FetchUserAttributesOutput,
): BasicUserInformationFormData {
  if (!initialData) {
    return {
      firstName: "",
      lastName: "",
      email: "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  return {
    firstName: initialData.given_name ?? "",
    lastName: initialData.family_name ?? "",
    email: initialData.email ?? "",
    timeZone: initialData.timeZone || null,
  };
}

export function BasicUserInformationForm(props: BasicUserInformationFormProps) {
  const { initialData } = props;
  const form = useForm<BasicUserInformationFormData>({
    resolver: zodResolver(basicUserInformationSchema),
    defaultValues: transformApiDataToFormData(initialData),
  });
  const { mutateAsync: updateUser } = useUpdateUser({
    onSuccess: (result) => {
      form.reset(result);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  async function handleUpdateUserInformation(
    data: BasicUserInformationFormData,
  ) {
    try {
      await updateUser({
        userId: initialData.sub ?? "",
        user: {
          ...data,
          ...(data.email ? { email: data.email.toLowerCase().trim() } : {}),
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateUserInformation)}>
          <Card.Header
            title="Basic Information"
            description="Manage user profile"
          />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>First Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Email</FormLabel>
                  <FormControl>
                    <Input disabled {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {props.isEditingOwnUser && (
              <FormField
                control={form.control}
                name="timeZone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isRequired>Timezone</FormLabel>
                    <FormDescription>
                      The timezone you select here will be used, when it's not
                      possible to use your browser's timezone, for example in
                      email notifications.
                    </FormDescription>
                    <FormControl>
                      <Select<TimezoneOption>
                        unstyled
                        components={{
                          DropdownIndicator: SearchIndicator,
                          ClearIndicator: () => null,
                          IndicatorSeparator: () => null,
                        }}
                        classNames={
                          createClassNames({}) as ClassNamesConfig<
                            TimezoneOption,
                            false,
                            GroupBase<TimezoneOption>
                          >
                        }
                        name="timeZone"
                        options={timezoneOptions}
                        placeholder="Select timezone"
                        noOptionsMessage={() =>
                          "No timezones found for your search"
                        }
                        value={timezoneOptions.find(
                          (option) => option.value === field.value,
                        )}
                        onChange={(option) => field.onChange(option?.value)}
                        isDisabled={form.formState.isSubmitting}
                        isSearchable
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      The timezone set on your device is:{" "}
                      {Intl.DateTimeFormat().resolvedOptions().timeZone}
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}
            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  form.reset(transformApiDataToFormData(initialData))
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
